import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { QuantityPerSizeStepDto, StepEnum } from '@/api-sdk';
import { iconCheckmark } from '@/components/icons';
import { ConfiguratorContextType, configuratorGlobal } from '@/context';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, loadingStyles, quantityTableStyles } from '@/styles';
import { getQuantityA11yName } from '@/utils/utils.accessibility';
import { QuantityTableMixin } from './quantity-table-mixin';

import '../../../configuration/hsp/fg-hsp-label';
import './fg-quantity-table-quantity-input';

@customElement('fg-quantity-per-size-table')
@localized()
export class FgQuantityPerSizeTable extends QuantityTableMixin(LitElement) {
  @property({ type: Array })
  options: QuantityPerSizeStepDto['options'] = [];

  @property({ type: Number })
  totalQuantity = 0;

  @property({ type: Number })
  hspMaximumQuantity?: number | string | null = 0;

  @property({ type: String })
  totalQuantityDescription?: string | null;

  @property({ type: String })
  totalPrice?: string | null;

  @property({ type: Boolean })
  loading = false;

  @property({ type: String })
  a11yDescription = '';

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  handleQuantitySet(event: CustomEvent) {
    this.handleQuantitySetAnnouncement(event, this.options);
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    const stepType = this.configuratorContext.quantityCollapsible
      ? StepEnum.quantity_per_size_collapsible
      : StepEnum.quantity_per_size;

    this.checkTotalPriceAndAnnounce(
      changedProperties,
      this.totalPrice,
      this.options,
      stepType,
      {
        fastDelivery: Boolean(this.hspMaximumQuantity),
      },
    );
  }

  render() {
    const { quantityCollapsible } = this.configuratorContext;
    return html`
      <p id="a11y-quantity-table-description" class="sr-only">
        ${this.a11yDescription}
      </p>
      <table
        aria-describedby="a11y-quantity-table-description"
        class="quantities size  ${classMap({
          collapsible: Boolean(quantityCollapsible),
        })}"
      >
        <thead>
          <tr>
            <th class="amount">${t('labels.quantity')}</th>
            <th class="size">${t('labels.size')}</th>
            <th class="mobile-hidden">${t('labels.pricePerItem')}</th>
            <th class="mobile-show">${t('labels.pricePerItemMb')}</th>
            <th>${t('labels.total')}</th>
          </tr>
        </thead>

        <tbody>
          ${this.options &&
          this.options.map(option => {
            const { queryString, quantity, size, totalPrice, unitPrice } =
              option;
            const a11yName = getQuantityA11yName(
              quantityCollapsible
                ? StepEnum.quantity_per_size_collapsible
                : StepEnum.quantity_per_size,
              {
                size,
                unitPrice,
              },
            );

            return html`
              <tr
                class=${classMap({
                  loading: this.loading,
                  active: quantity > 0,
                })}
              >
                <td class="amount">
                  <span class="checkmark-wrapper">
                    <span
                      class="icon-wrapper ${classMap({
                        collapsible: Boolean(quantityCollapsible),
                      })}"
                    >
                      <span
                        class="checkmark${classMap({
                          selected: quantity > 0,
                        })}"
                      >
                        ${iconCheckmark}
                      </span>
                    </span>
                    <fg-quantity-table-quantity-input
                      .optionQueryString=${queryString}
                      .name=${a11yName}
                      .title=${a11yName}
                      .quantity=${quantity}
                      .showQuantityIncrementButtons=${quantityCollapsible
                        ? false
                        : true}
                      @quantity-set=${this.handleQuantitySet}
                    ></fg-quantity-table-quantity-input>
                  </span>
                </td>
                <td class="size">${size}</td>
                <td>
                  <span>${unitPrice}</span>
                </td>
                <td class="total-price">
                  <span>${totalPrice ?? '-'}</span>
                </td>
              </tr>
            `;
          })}
        </tbody>

        <tfoot>
          <tr class=${this.loading ? `loading` : ''}>
            <td colspan="4">
              <table>
                <tr class="total">
                  <td>
                    ${this.totalQuantityDescription
                      ? this.totalQuantityDescription
                      : t(
                          'content.configurator.chooseQuantityStep.totalQuantity',
                          {
                            quantity: this.totalQuantity,
                          },
                        )}
                  </td>
                  <td>
                    <span>${this.totalPrice ?? '-'}</span>
                  </td>
                </tr>
                ${this.hspMaximumQuantity
                  ? html`
                      <tr>
                        <td>
                          <fg-hsp-label
                            .hspMaximumQuantity=${this.hspMaximumQuantity}
                          ></fg-hsp-label>
                        </td>
                        <td></td>
                      </tr>
                    `
                  : nothing}
              </table>
            </td>
          </tr>
        </tfoot>
      </table>
    `;
  }

  static styles = [
    baseStyles,
    quantityTableStyles,
    loadingStyles,
    css`
      .mobile-hidden {
        display: none;
      }
      .mobile-show {
        display: table-cell;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .mobile-show {
          display: none;
        }
        .mobile-hidden {
          display: table-cell;
        }
      }
    `,
  ];
}
